<div class="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm" (click)="cancel()">
  <div
    class="animate-in fade-in zoom-in relative w-full max-w-[500px] overflow-hidden rounded-[20px] bg-white shadow-2xl duration-200"
    (click)="$event.stopPropagation()">
    <div class="flex items-center justify-between border-b border-gray-100 px-8 py-5">
      <h2 class="text-xl font-bold text-gray-800" i18n="@@createCartTitle">Create New Cart</h2>
      <button
        (click)="cancel()"
        class="cursor-pointer rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="p-8">
      <div class="flex flex-col gap-6">
        <div class="relative">
          <label class="mb-2 block text-sm font-semibold text-gray-700">
            <ng-container i18n="@@pharmacyLabel">Pharmacy</ng-container>
            <span class="text-red-500">*</span>
          </label>

          <div class="relative" (click)="$event.stopPropagation()">
            <div class="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-gray-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>

            <input
              type="text"
              autocomplete="off"
              [ngModel]="pharmacySearchQuery()"
              (ngModelChange)="onPharmacyInputChange($event)"
              (blur)="hideDropdown()"
              i18n-placeholder="@@pharmacySearchPlaceholder"
              placeholder="Search pharmacy..."
              [class]="
                pharmacyError()
                  ? 'w-full rounded-[10px] border-2 border-red-400 p-2 py-3 pl-10 transition-all outline-none focus:border-red-400 focus:ring-4 focus:ring-red-50'
                  : 'w-full rounded-[10px] border-2 border-gray-200 p-2 py-3 pl-10 transition-all outline-none focus:border-blue-400/50 focus:ring-4 focus:ring-blue-50'
              " />

            @if (pharmacyLoading()) {
              <div class="absolute top-1/2 right-3 -translate-y-1/2">
                <svg
                  class="h-4 w-4 animate-spin text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              </div>
            }

            @if (showDropdown()) {
              <div
                class="absolute z-50 mt-2 max-h-[240px] w-full overflow-y-auto rounded-[12px] bg-white shadow-xl ring-1 ring-black/5">
                @if (noResults()) {
                  <div class="px-5 py-4 text-center text-[13px] text-gray-400" i18n="@@noPharmaciesFound">
                    {{ isArabic() ? "لا توجد نتائج مطابقة" : "No matching results found." }}
                  </div>
                }
                @for (p of pharmacyResults(); track p.id) {
                  <div
                    (mousedown)="selectPharmacy(p, $event)"
                    class="group flex cursor-pointer items-center border-b border-gray-50 px-5 py-3 transition-all last:border-none hover:bg-gray-50">
                    <div class="flex min-w-0 flex-col">
                      <span class="truncate text-[13.5px] font-semibold text-gray-600 group-hover:text-gray-900">
                        {{ p.nickName }}
                      </span>
                      <span class="truncate text-[11px] text-gray-400">{{ p.address }}</span>
                    </div>
                  </div>
                }
              </div>
            }
          </div>

          @if (pharmacyError(); as msg) {
            <app-error [apiError]="msg"></app-error>
          }
        </div>

        @if (selectedPharmacy(); as pharmacy) {
          <div
            class="-mt-2 flex items-start justify-between gap-4 rounded-[12px] border border-blue-100 bg-blue-50/50 p-4">
            <div class="flex min-w-0 flex-col gap-1">
              <p class="truncate text-sm font-bold text-gray-800">{{ pharmacy.nickName }}</p>
              <p class="truncate text-[12px] text-gray-500">{{ pharmacy.address }}</p>
              <p class="truncate text-[11px] text-gray-400">{{ pharmacy.pharmacyOwner?.fullName }}</p>
            </div>
            <button
              (click)="clearPharmacySelection()"
              class="textTabsColor shrink-0 cursor-pointer text-[12px] font-bold underline hover:text-blue-700"
              i18n="@@changeBtn">
              Change
            </button>
          </div>
        }

        <div>
          <label class="mb-2 block text-sm font-semibold text-gray-700">
            <ng-container i18n="@@cartTitleLabel">Cart Title</ng-container>
            <span class="ml-1 text-[11px] font-normal text-gray-400" i18n="@@optionalHint">(Optional)</span>
          </label>

          <input
            type="text"
            maxlength="151"
            [ngModel]="cartTitle()"
            (ngModelChange)="cartTitle.set($event); cartTitleTouched.set(true)"
            i18n-placeholder="@@cartTitlePlaceholder"
            placeholder="Enter cart title"
            [class]="
              cartTitleError()
                ? 'w-full rounded-[10px] border-2 border-red-400 p-2 py-3 transition-all outline-none focus:border-red-400 focus:ring-4 focus:ring-red-50'
                : 'w-full rounded-[10px] border-2 border-gray-200 p-2 py-3 transition-all outline-none focus:border-blue-400/50 focus:ring-4 focus:ring-blue-50'
            " />

          <div class="mt-1 flex items-center justify-between">
            @if (cartTitleError(); as msg) {
              <app-error [apiError]="msg"></app-error>
            } @else {
              <span></span>
            }
            <span
              class="ml-auto text-[11px]"
              [class]="cartTitle().length > 150 ? 'font-bold text-red-400' : 'text-gray-400'">
              {{ cartTitle().length }}/150
            </span>
          </div>
        </div>

        @if (apiError(); as errorMessage) {
          <app-error [apiError]="errorMessage"></app-error>
        }
      </div>

      <div class="mt-10 flex gap-4">
        <button
          (click)="cancel()"
          [disabled]="loading()"
          class="flex-1 cursor-pointer rounded-[10px] border-2 border-gray-200 py-3 text-[13px] font-bold text-gray-500 uppercase transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          i18n="@@cancelBtn">
          CANCEL
        </button>
        <button
          (click)="confirm()"
          [disabled]="loading() || !isFormValid()"
          class="bgTabsColor hover-bg-primary-hover flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-[10px] py-3 text-[13px] font-bold text-white uppercase shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-50">
          @if (loading()) {
            <svg
              class="h-4 w-4 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <span i18n="@@creatingStatus">Creating...</span>
          } @else {
            <span i18n="@@submitBtn">SUBMIT</span>
          }
        </button>
      </div>
    </div>
  </div>
</div>
